import {
  hubItemDtoFromListing,
  isContentRating,
  normalizeHubItemCreator,
  normalizeHubItemStats,
  type HubClient,
  type ContentRating,
  type HubItemDisplayInput,
  type HubItemDto,
  type HubListing,
  type HubPage,
  type HubQuery,
} from "@roqeet/core";

export interface HubCardViewModel {
  item: HubItemDto;
  legacyListing?: HubListing;
  canDownload: boolean;
  creator: HubCardCreatorViewModel;
  stats: HubCardStatsViewModel;
}

export interface HubCardCreatorViewModel {
  id: string;
  displayName: string;
  handle?: string;
  avatarUrl?: string;
}

export interface HubCardStatsViewModel {
  views: number;
  downloads: number;
  bookmarks: number;
  comments: number;
  ratingAverage: number | null;
  ratingCount: number;
}

export function hubSupportsLegacyDownloads(hub: HubClient): boolean {
  return hub.supportsLegacyDownloads?.() === true;
}

export function hubItemDisplayInput(item: HubItemDto): HubItemDisplayInput {
  return {
    itemKind: item.kind,
    ...(item.botSubtype !== undefined ? { botSubtype: item.botSubtype } : {}),
    ...(item.parentItemId !== undefined ? { parentItemId: item.parentItemId } : {}),
  };
}

export function hubItemRating(item: HubItemDto): ContentRating {
  return item.rating && isContentRating(item.rating) ? item.rating : "unrated";
}

export function hubItemPassesRatingGate(item: HubItemDto, includeNsfw: boolean): boolean {
  return includeNsfw || hubItemRating(item) !== "nsfw";
}

export function buildHubCardViewModels(
  items: HubItemDto[],
  legacyListings: HubListing[] = [],
): HubCardViewModel[] {
  const legacyById = new Map(legacyListings.map((listing) => [listing.id, listing]));
  return items.map((item) => {
    const legacyListing = legacyById.get(item.id);
    return {
      item,
      ...(legacyListing ? { legacyListing } : {}),
      canDownload: !!legacyListing,
      creator: hubCardCreator(item, legacyListing),
      stats: hubCardStats(item, legacyListing),
    };
  });
}

export function hubCardCreator(
  item: HubItemDto,
  legacyListing?: HubListing,
): HubCardCreatorViewModel {
  const creator = normalizeHubItemCreator(item.ownerId, {
    ...(item.creator ?? {}),
    handle: item.creator?.handle ?? legacyListing?.author.handle,
    avatarUrl: item.creator?.avatarUrl ?? legacyListing?.author.avatarUrl,
  });
  return {
    id: creator.id,
    displayName: creator.displayName,
    ...(creator.handle ? { handle: creator.handle } : {}),
    ...(creator.avatarUrl ? { avatarUrl: creator.avatarUrl } : {}),
  };
}

export function hubCardStats(
  item: HubItemDto,
  legacyListing?: HubListing,
): HubCardStatsViewModel {
  const stats = normalizeHubItemStats({
    views: item.stats?.views ?? legacyListing?.viewCount,
    downloads: item.stats?.downloads ?? legacyListing?.downloadCount,
    bookmarks: item.stats?.bookmarks ?? legacyListing?.bookmarkCount,
    comments: item.stats?.comments ?? legacyListing?.commentCount,
    ratingAverage: item.stats?.ratingAverage ?? legacyListing?.ratingAvg,
    ratingCount: item.stats?.ratingCount ?? legacyListing?.ratingCount,
    installs: item.stats?.installs,
  });
  return {
    views: stats.views,
    downloads: stats.downloads,
    bookmarks: stats.bookmarks,
    comments: stats.comments,
    ratingAverage: stats.ratingAverage,
    ratingCount: stats.ratingCount,
  };
}

export async function loadHubCardPage(
  hub: HubClient,
  query: HubQuery,
  includeNsfw: boolean,
): Promise<HubPage<HubCardViewModel>> {
  if (hubSupportsLegacyDownloads(hub)) {
    const page = await hub.list(query);
    const legacyListings = page.items.filter(
      (listing) => includeNsfw || listing.rating !== "nsfw",
    );
    const result: HubPage<HubCardViewModel> = {
      items: buildHubCardViewModels(
        legacyListings.map(hubItemDtoFromListing),
        legacyListings,
      ),
    };
    if (page.nextCursor) result.nextCursor = page.nextCursor;
    return result;
  }

  const page = await hub.listHubItems(query);
  const result: HubPage<HubCardViewModel> = {
    items: buildHubCardViewModels(
      page.items.filter((item) => hubItemPassesRatingGate(item, includeNsfw)),
    ),
  };
  if (page.nextCursor) result.nextCursor = page.nextCursor;
  return result;
}
